/// <reference types="react" />
import React from "react";
import { createPortal } from "react-dom";
import "./or-dropdown.css";
import { useOrDropdown } from "./or-dropdown.hook";

export type OrDropdownVariant = "default" | "soft" | "ghost";
export type OrDropdownTheme = "default" | "social";
export type OrDropdownSize = "sm" | "md";

type ListPlacement = "bottom" | "top";

export interface OrDropdownProps<TOption> {
  className?: string;
  disableClearable?: boolean;
  disabled?: boolean;
  disablePortal?: boolean;
  errorText?: React.ReactNode;
  fullWidth?: boolean;
  getOptionLabel?: (option: TOption) => string;
  hasError?: boolean;
  helperText?: React.ReactNode;
  id?: string;
  inputValue?: string;
  isOptionEqualToValue?: (option: TOption, value: TOption) => boolean;
  label?: React.ReactNode;
  leftSlot?: React.ReactNode;
  loading?: boolean;
  loadingText?: React.ReactNode;
  multiple?: boolean;
  noOptionsText?: React.ReactNode;
  onChange?: (event: React.SyntheticEvent | null, value: TOption | TOption[] | null) => void;
  onInputChange?: (event: React.ChangeEvent<HTMLInputElement>, newInputValue: string) => void;
  options: readonly TOption[];
  placeholder?: string;
  renderOption?: (props: React.HTMLAttributes<HTMLLIElement>, option: TOption) => React.ReactNode;
  rightSlot?: React.ReactNode;
  size?: OrDropdownSize;
  theme?: OrDropdownTheme;
  value?: TOption | TOption[] | null;
  variant?: OrDropdownVariant;
}

function getResolvedOptionLabel<TOption>(option: TOption, getOptionLabel?: (option: TOption) => string): string {
  if (getOptionLabel) {
    return getOptionLabel(option);
  }

  if (typeof option === "string") {
    return option;
  }

  if (typeof option === "object" && option !== null && "label" in option) {
    const { label } = option;

    return typeof label === "string" ? label : "";
  }

  return "";
}

function getMultipleSelectionPlaceholder<TOption>(
  selectedOptions: readonly TOption[],
  getOptionLabel?: (option: TOption) => string,
): string {
  const selectedLabels = selectedOptions.map((option) => getResolvedOptionLabel(option, getOptionLabel)).filter(Boolean);

  if (selectedLabels.length === 0) {
    return "";
  }

  const visibleLabels = selectedLabels.slice(0, 2).join(", ");
  const suffix = selectedLabels.length > 2 ? " ..." : "";

  return `Selección: ${visibleLabels}${suffix}`;
}

export function OrDropdown<TOption>({
  className,
  disableClearable = false,
  disabled,
  disablePortal = false,
  errorText,
  fullWidth = true,
  getOptionLabel,
  hasError = false,
  helperText,
  id,
  inputValue,
  isOptionEqualToValue,
  label,
  leftSlot,
  loading,
  loadingText = "Cargando...",
  multiple,
  noOptionsText = "Sin resultados",
  onChange,
  onInputChange,
  options,
  placeholder,
  renderOption,
  rightSlot,
  size,
  theme,
  value,
  variant,
}: OrDropdownProps<TOption>): React.ReactElement {
  const MAX_LIST_HEIGHT = 400;
  const MIN_LIST_HEIGHT = 96;
  const ESTIMATED_LIST_HEIGHT = 240;
  const VIEWPORT_PADDING = 16;
  const VERTICAL_OFFSET = 6;
  const generatedId = React.useId();
  const inputId = id ?? generatedId;
  const listId = `${inputId}-listbox`;
  const controlRef = React.useRef<HTMLDivElement | null>(null);
  const [inlineListElement, setInlineListElement] = React.useState<HTMLUListElement | null>(null);
  const [portalListElement, setPortalListElement] = React.useState<HTMLUListElement | null>(null);
  const interactiveRefs = React.useMemo<readonly { readonly current: HTMLElement | null }[]>(
    () => [{ current: portalListElement }],
    [portalListElement],
  );
  const [listPlacement, setListPlacement] = React.useState<ListPlacement>("bottom");
  const [inlineListStyle, setInlineListStyle] = React.useState<React.CSSProperties>();
  const [portalStyle, setPortalStyle] = React.useState<React.CSSProperties>();
  const handleInlineListRef = React.useCallback((node: HTMLUListElement | null): void => {
    setInlineListElement(node);
  }, []);
  const handlePortalListRef = React.useCallback((node: HTMLUListElement | null): void => {
    setPortalListElement(node);
  }, []);
  const {
    clearInputValue,
    containerRef,
    controlClassName,
    displayValue,
    filteredOptions,
    handleBlur,
    handleFocus,
    handleInputChange,
    handleKeyDown,
    handleOptionClick,
    highlightedIndex,
    inputClassName,
    inputRef,
    isOpen,
    listClassName,
    optionClassName,
    setHighlightedIndex,
    setIsOpen,
    wrapperClassName,
  } = useOrDropdown({
    className,
    disabled,
    fullWidth,
    getOptionLabel,
    hasError,
    inputValue,
    interactiveRefs: disablePortal ? [] : interactiveRefs,
    isOptionEqualToValue,
    loading,
    multiple,
    onChange,
    onInputChange,
    options,
    value,
    size,
    theme,
    variant,
  });

  const showNoOptions = isOpen && !loading && filteredOptions.length === 0;
  const hasValue = Array.isArray(value) ? value.length > 0 : value !== null && value !== undefined && value !== "";
  const shouldRenderList = isOpen && (loading || filteredOptions.length > 0 || showNoOptions);
  const shouldUsePortal = shouldRenderList && !disablePortal && typeof document !== "undefined";
  const placeholderText =
    multiple && Array.isArray(value) && value.length > 0 && !displayValue
      ? getMultipleSelectionPlaceholder(value, getOptionLabel)
      : placeholder;

  const handleClear = (event: React.MouseEvent<HTMLButtonElement>): void => {
    event.preventDefault();
    event.stopPropagation();

    clearInputValue();
    setIsOpen(true);
    onChange?.(null, multiple ? [] : null);
    inputRef.current?.focus();
  };

  const resolveListPosition = React.useCallback((listHeight: number) => {
    if (typeof window === "undefined" || !controlRef.current) {
      return null;
    }

    const controlRect = controlRef.current.getBoundingClientRect();
    const constrainedWidth = Math.min(controlRect.width, window.innerWidth - VIEWPORT_PADDING * 2);
    const left = Math.min(
      Math.max(controlRect.left, VIEWPORT_PADDING),
      Math.max(VIEWPORT_PADDING, window.innerWidth - constrainedWidth - VIEWPORT_PADDING),
    );
    const availableBelow = window.innerHeight - controlRect.bottom - VIEWPORT_PADDING - VERTICAL_OFFSET;
    const availableAbove = controlRect.top - VIEWPORT_PADDING - VERTICAL_OFFSET;
    const desiredHeight = Math.min(Math.max(listHeight, ESTIMATED_LIST_HEIGHT), MAX_LIST_HEIGHT);
    const shouldOpenAbove = availableBelow < desiredHeight && availableAbove > availableBelow;
    const availableHeight = shouldOpenAbove ? availableAbove : availableBelow;
    const placement: ListPlacement = shouldOpenAbove ? "top" : "bottom";

    return {
      availableHeight: Math.max(MIN_LIST_HEIGHT, Math.min(availableHeight, MAX_LIST_HEIGHT)),
      constrainedWidth,
      controlRect,
      left,
      placement,
    };
  }, []);

  const updatePortalPosition = React.useCallback(() => {
    const resolvedPosition = resolveListPosition(portalListElement?.offsetHeight || 0);

    if (!resolvedPosition) {
      return;
    }

    setListPlacement(resolvedPosition.placement);

    setPortalStyle({
      left: resolvedPosition.left,
      maxHeight: resolvedPosition.availableHeight,
      top:
        resolvedPosition.placement === "top"
          ? resolvedPosition.controlRect.top - VERTICAL_OFFSET
          : resolvedPosition.controlRect.bottom + VERTICAL_OFFSET,
      transform: resolvedPosition.placement === "top" ? "translateY(-100%)" : undefined,
      width: resolvedPosition.constrainedWidth,
    });
  }, [portalListElement, resolveListPosition]);

  const updateInlinePosition = React.useCallback(() => {
    const resolvedPosition = resolveListPosition(inlineListElement?.offsetHeight || 0);

    if (!resolvedPosition) {
      return;
    }

    setListPlacement(resolvedPosition.placement);
    setInlineListStyle({
      maxHeight: resolvedPosition.availableHeight,
    });
  }, [inlineListElement, resolveListPosition]);

  React.useEffect(() => {
    if (!shouldUsePortal) {
      return;
    }

    updatePortalPosition();
    window.addEventListener("resize", updatePortalPosition);
    window.addEventListener("scroll", updatePortalPosition, true);

    return () => {
      window.removeEventListener("resize", updatePortalPosition);
      window.removeEventListener("scroll", updatePortalPosition, true);
    };
  }, [shouldUsePortal, updatePortalPosition]);

  React.useEffect(() => {
    if (!shouldRenderList || shouldUsePortal) {
      return;
    }

    updateInlinePosition();
    window.addEventListener("resize", updateInlinePosition);
    window.addEventListener("scroll", updateInlinePosition, true);

    return () => {
      window.removeEventListener("resize", updateInlinePosition);
      window.removeEventListener("scroll", updateInlinePosition, true);
    };
  }, [shouldRenderList, shouldUsePortal, updateInlinePosition]);

  const renderDropdownList = (
    extraClassName?: string,
    listRef?: React.Ref<HTMLUListElement>,
    style?: React.CSSProperties,
  ): React.ReactElement => {
    const isAllSelected = multiple && Array.isArray(value) && value.length === options.length && options.length > 0;

    return (
      <ul
        className={[
          listClassName,
          extraClassName,
          theme === "social" ? "or-dropdown__list--social" : "",
          listPlacement === "top" ? "or-dropdown__list--top" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        id={listId}
        ref={listRef}
        role="listbox"
        style={style}
      >
        {loading ? (
          <li className="or-dropdown__option or-dropdown__option--loading">{loadingText}</li>
        ) : showNoOptions ? (
          <li className="or-dropdown__option or-dropdown__option--no-options">{noOptionsText}</li>
        ) : (
          <>
            {multiple && options.length > 0 && !inputValue && (
              <li
                className={`or-dropdown__option ${isAllSelected ? "or-dropdown__option--selected" : ""}`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (isAllSelected) {
                    onChange?.(null, []);
                  } else {
                    onChange?.(null, options.slice());
                  }
                  inputRef.current?.focus();
                }}
                onMouseDown={(e) => e.preventDefault()}
                role="option"
                aria-selected={isAllSelected}
              >
                <span>
                  Seleccionar todos
                  {Array.isArray(value) && value.length > 0 && (
                    <span className="or-dropdown__select-all-count">
                      ({value.length} seleccionado{value.length > 1 ? "s" : ""})
                    </span>
                  )}
                </span>
              </li>
            )}
            {filteredOptions.map((option, index) => {
              const optionLabel = getResolvedOptionLabel(option, getOptionLabel);
              const isSelected = Array.isArray(value)
                ? value.some((currentValue) =>
                    isOptionEqualToValue ? isOptionEqualToValue(option, currentValue) : option === currentValue,
                  )
                : value !== null && value !== undefined && !Array.isArray(value)
                  ? isOptionEqualToValue
                    ? isOptionEqualToValue(option, value)
                    : option === value
                  : false;

              const optionProps = {
                "aria-selected": isSelected,
                className: `${optionClassName(index)} ${isSelected ? "or-dropdown__option--selected" : ""}`,
                id: `${inputId}-option-${index}`,
                key: index,
                onClick: () => handleOptionClick(option),
                onMouseDown: (event: React.MouseEvent<HTMLLIElement>) => {
                  event.preventDefault();
                },
                onMouseEnter: () => {
                  setIsOpen(true);
                  setHighlightedIndex(index);
                },
                role: "option" as const,
              };

              const content = (
                <>
                  {optionLabel}
                  {multiple && isSelected && <span className="or-dropdown__option-x">×</span>}
                </>
              );

              if (renderOption) {
                return renderOption(optionProps, option);
              }

              return <li {...optionProps}>{content}</li>;
            })}
          </>
        )}
      </ul>
    );
  };

  return (
    <div className={wrapperClassName} onBlur={handleBlur} ref={(node) => {
      containerRef.current = node;
    }}>
      {multiple && Array.isArray(value) && value.length > 0 && (
        <div className="or-dropdown__badges or-dropdown__badges--outside">
          {value.map((item, idx) => (
            <span
              key={idx}
              className="or-dropdown__badge"
              onClick={(e) => {
                e.stopPropagation();
                handleOptionClick(item);
              }}
            >
              {getResolvedOptionLabel(item, getOptionLabel)}
              <button
                type="button"
                className="or-dropdown__badge-remove"
                onClick={(e) => {
                  e.stopPropagation();
                  handleOptionClick(item);
                }}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      {label ? (
        <label className="or-dropdown__label" htmlFor={inputId}>
          {label}
        </label>
      ) : null}

      <div className={controlClassName} ref={(node) => {
          controlRef.current = node;
        }}>
        {leftSlot ? <span className="or-dropdown__slot or-dropdown__slot--left">{leftSlot}</span> : null}

        <input
          aria-activedescendant={highlightedIndex >= 0 ? `${inputId}-option-${highlightedIndex}` : undefined}
          aria-autocomplete="list"
          aria-controls={isOpen ? listId : undefined}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          autoComplete="off"
          className={inputClassName}
          disabled={disabled}
          id={inputId}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholderText}
          ref={(node) => {
          inputRef.current = node;
        }}
          role="combobox"
          type="search"
          value={displayValue}
        />
        <div className="or-dropdown__actions">
          {rightSlot ? <span className="or-dropdown__slot or-dropdown__slot--right">{rightSlot}</span> : null}
          {!disableClearable && hasValue ? (
            <button
              aria-label="Limpiar selección"
              className="or-dropdown__clear"
              disabled={disabled}
              onClick={handleClear}
              type="button"
            >
              <svg
                aria-hidden="true"
                fill="none"
                height="16"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                viewBox="0 0 24 24"
                width="16"
              >
                <line x1="6" x2="18" y1="6" y2="18" />
                <line x1="18" x2="6" y1="6" y2="18" />
              </svg>
            </button>
          ) : null}
          <button
            aria-label={isOpen ? "Cerrar opciones" : "Abrir opciones"}
            className="or-dropdown__indicator"
            disabled={disabled}
            onClick={() => !disabled && setIsOpen(!isOpen)}
            type="button"
          >
            <svg
              fill="none"
              height="20"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              width="20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>
        </div>
      </div>

      {shouldRenderList
        ? shouldUsePortal
          ? createPortal(
              renderDropdownList("or-dropdown__list--portal", handlePortalListRef, portalStyle),
              document.body,
            )
          : renderDropdownList(undefined, handleInlineListRef, inlineListStyle)
        : null}

      {hasError && errorText ? <p className="or-dropdown__message or-dropdown__message--error">{errorText}</p> : null}
      {!hasError && helperText ? <p className="or-dropdown__message">{helperText}</p> : null}
    </div>
  );
}
